import { UserImageView } from "@src/components/deprecated/UserImageView/UserImageView";
import { getFunctions, httpsCallable } from "firebase/functions";
import { t } from "i18next";
import { DataTableData } from "../../../../../components/deprecated/DataTable/DataTable";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { UserNameLabel } from "../../../../../components/deprecated/Text/Label/UserNameLabel";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { ActionValidator } from "../../../../../auth/actionValidator";
import { UserData } from "../../../User/UserData";
import { FirebaseDataCompanyField } from "../../DataObjectField/CustomFields/FirebaseDataCompanyField";
import { FirebaseDataFirebaseFileField } from "../../DataObjectField/CustomFields/FirebaseDataFirebaseFileField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";

const functions = getFunctions(undefined, "europe-west1");
const resendUserInvitation = httpsCallable(functions, "resendUserInvitation");

export class UserDataHelper extends DataObjectHelper<UserData> {
  id = () => "user";
  displayName = () => t("main.personC");

  profileImage: FirebaseDataFirebaseFileField<UserData> = new FirebaseDataFirebaseFileField<UserData>(
    "profileImage",
    "",
    (data) => data.profileImage_Small,
    ""
  );
  name = new FirebaseDataStringField<UserData>("name", t("main.name"), (user) => user.name, "abc");
  email = new FirebaseDataStringField<UserData>("email", t("main.email"), (user) => user.email, "mail");
  phone = new FirebaseDataStringField<UserData>("phone", t("main.phone"), (user) => user.phone, "phone");
  lastLogin = new FirebaseDataDateField<UserData>("lastLogin", t("main.lastLogin"), (user) => user.lastLogin, "shield");
  companies = new FirebaseDataCompanyField<UserData>("companies", t("main.companies"), (user) => user.companies, "real_estate_agent", true);
  properties = new FirebaseDataPropertyField<UserData>("properties", t("main.properties"), (user) => user.properties, "apartment", true);
  hasAuth = new FirebaseDataBooleanField<UserData>("hasAuth", t("main.hasLoginDetails"), (user) => user.hasAuth, "shield");

  allFields() {
    return [this.profileImage, this.name, this.email, this.phone, this.lastLogin, this.companies, this.properties, this.hasAuth];
  }

  override getAllDatas(): UserData[] {
    const users: UserData[] = [];
    for (const property of LocalData.currentUser.properties) {
      for (const member of property.members.documents) {
        if (!users.includes(member.user)) {
          users.push(member.user);
        }
      }
    }
    return users;
  }

  override loadAll() {
    const allUsers: UserData[] = [];
    const allPromises: Promise<any>[] = [];
    for (const property of LocalData.currentUser.properties) {
      const promise = new Promise((resolve) => {
        property.members.load((members) => {
          for (const member of members) {
            if (!allUsers.includes(member.user)) {
              allUsers.push(member.user);
            }
          }
          resolve(allUsers);
        });
      });
      allPromises.push(promise);
    }
    return Promise.all(allPromises).then(() => {
      const newAllPromises: Promise<any>[] = [];
      allUsers.forEach((user) => {
        if (!user.loaded) {
          newAllPromises.push(user.load());
        }
      });
      return Promise.all(newAllPromises);
    });
  }

  getAllDataTableDatas(documents: UserData[]): DataTableData[] {
    return documents.map((user) => {
      return {
        key: user.id,
        cells: {
          profileImage: { element: <UserImageView size={30} user={user} /> },
          name: { element: <UserNameLabel user={user} type="paragraph" disableUserPopUp />, rawString: user.name },
          email: user.email,
          phone: user.phone,
          lastLogin: user.lastLogin,
          properties: user.properties
            .filter((p) => p.members.hasData(LocalData.currentUser.id))
            .map((p) => p.name)
            .sort((a, b) => a.localeCompare(b))
            .join(", "),
          hasAuth: user.hasAuth ? t("main.yes") : t("main.no"),
        },
      };
    });
  }

  getLinkTo(key: string): string | undefined {
    if (ActionValidator.isAllowedToPerformAction("userspage_access")) {
      return "/users/" + key;
    }

    return "";
  }
  getDefaultTableSortKey(): string | undefined {
    return "name";
  }

  override getActionsForData = (data: UserData): DataObjectAction<UserData>[] => {
    const actions: DataObjectAction<UserData>[] = [];

    actions.push({
      id: "send_native_email",
      title: t("main.sendEmail"),
      iconName: "email",
      performActionOnDataObjects: (objects) => {
        console.log(objects);
        const emails = objects.map((object) => object.email).join(";");
        window.location.href = "mailto:" + emails;
        return Promise.resolve();
      },
    });
    actions.push({
      id: "send_native_sms",
      title: t("main.sendSMS"),
      iconName: "sms",
      maxSelected: 1,
      performAction: () => {
        window.location.href = "sms:" + data.phone;
        return Promise.resolve();
      },
    });

    if (ActionValidator.isAllowedToPerformAction("user_update_name", data.properties)) {
      actions.push({
        id: "user_update_name",
        title: t("main.editUserName"),
        iconName: "drive_file_rename_outline",
        maxSelected: 1,
        performAction: () =>
          new Promise((resolve) =>
            PopupTemplates.presentTextInput({
              title: t("main.editUserName"),
              placeholder: data.name,
              onConfirm: (popupId, name) => {
                Popup.remove(popupId);
                ToastUtilities.presentSavingToast(
                  data.saveUpdate({ name }).then(() => {
                    data.name = name;
                    resolve(undefined);
                  })
                );
              },
              actionTitle: t("main.confirm"),
              defaultValue: data.name,
            })
          ),
      });
    }

    if (ActionValidator.isAllowedToPerformAction("user_update_phone", data.properties)) {
      actions.push({
        id: "user_update_phone",
        title: t("main.editPhoneNumber"),
        iconName: "drive_file_rename_outline",
        maxSelected: 1,
        performAction: () =>
          new Promise((resolve) => {
            PopupTemplates.presentPhoneNumberInput(
              t("main.editPhoneNumber"),
              data.phone,
              (popupId, newPhone) => {
                Popup.remove(popupId);
                ToastUtilities.presentSavingToast(data.saveUpdate({ phone: newPhone })).then(() => {
                  data.phone = newPhone;
                  resolve(undefined);
                });
              },
              t("main.confirm"),
              undefined,
              data.phone
            );
          }),
      });
    }

    if (!data.hasAuth) {
      actions.push({
        id: "user_send_login_details",
        title: t("main.sendLoginDetails"),
        iconName: "lock_reset",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyAction(t("main.verifyAction"), t("main.areYouSureAboutSendingUserInvitation"), () => {
              resolve({ dataObjects: dataObjects });
            });
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(
            resendUserInvitation({
              userID: data.id,
            })
          );
        },
      });
    } else if (!data.lastLogin) {
      actions.push({
        id: "user_resend_login_details",
        title: t("main.resendLoginDetails"),
        iconName: "lock_reset",
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyAction(t("main.verifyAction"), t("main.areYouSureAboutResending"), () => {
              resolve({ dataObjects: dataObjects });
            });
          });
        },
        performAction: () => {
          return ToastUtilities.presentSavingToast(
            resendUserInvitation({
              userID: data.id,
            })
          );
        },
      });
    }

    if (ActionValidator.isAllowedToPerformAction("userspage_access")) {
      actions.push({
        id: "user_view",
        maxSelected: 1,
        title: t("main.administrate"),
        iconName: "drive_file_rename_outline",
        performAction: () => {
          window.location.href = "/users/" + data.id;
          return Promise.resolve();
        },
      });
    }

    return actions;
  };
}
